<template>
  <div class="min-h-screen">
    <!-- SEO Meta Tags -->
    <PageSEO
      title="API Development Services - Custom API Solutions"
      description="Custom API development and integration services for seamless data exchange and third-party connectivity. We build robust, scalable APIs that power your applications."
      :keywords="['API development', 'REST API', 'GraphQL', 'API integration', 'third-party APIs', 'API documentation', 'web services']"
      url="https://vedware.com/services/api-development"
    />

    <!-- Hero Section -->
    <HeroSection
      :icon="PhLightning"
      title="API"
      highlighted-title="Development"
      description="Custom API development and integration services for seamless data exchange and third-party connectivity. We build robust, scalable APIs that power your applications."
    />

    <!-- Service Overview -->
    <ServiceOverview
      :description="[
        'Our API development service focuses on creating robust, well-documented, and scalable APIs that enable seamless communication between different systems and applications.',
        'From RESTful APIs to GraphQL endpoints, we design and implement APIs that are secure, performant, and easy to integrate. We also provide comprehensive documentation and testing to ensure reliability.',
      ]"
      :features="[
        'RESTful and GraphQL API design',
        'Comprehensive API documentation',
        'Authentication and authorization',
        'Rate limiting and throttling',
        'Third-party API integration',
        'Automated testing and monitoring',
      ]"
    />

    <!-- Technologies Section -->
    <ServiceTechnologies
      title="API Technologies"
      description="We use modern API technologies and tools to build reliable, scalable, and well-documented APIs."
      :categories="technologyCategories"
    />

    <!-- Services Offered -->
    <ServiceOfferings
      title="API Services"
      description="We provide comprehensive API development and integration services to connect your systems."
      :services="apiServices"
    />

    <ServiceProjects slug="api-development" />

    <!-- CTA Section -->
    <ServiceCTA
      title="Ready to Build Powerful"
      highlighted-title="APIs?"
      description="Let's create APIs that seamlessly connect your systems and enable powerful integrations."
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from '#imports'
import {
  PhActivity,
  PhBook,
  PhGitBranch,
  PhLightning,
  PhLink,
  PhShield,
  PhStack,
} from '@phosphor-icons/vue'
import ServiceCTA from '~/components/Services/ServiceCTA.vue'
import ServiceOfferings from '~/components/Services/ServiceOfferings.vue'
import ServiceOverview from '~/components/Services/ServiceOverview.vue'
import ServiceProjects from '~/components/Services/ServiceProjects.vue'
import ServiceTechnologies from '~/components/Services/ServiceTechnologies.vue'
import HeroSection from '~/components/Shared/HeroSection.vue'
import PageSEO from '~/components/Shared/PageSEO.vue'

const technologyCategories = ref([
  {
    title: 'API Types',
    icon: PhLink,
    technologies: [
      'REST APIs',
      'GraphQL',
      'JSON',
      'XML',
      'Webhooks',
      'WebSockets',
      'Socket.IO',
      'Push Notifications',
    ],
  },
  {
    title: 'Authentication',
    icon: PhShield,
    technologies: [
      'OAuth 2',
      'JWT',
      'API Keys',
      'Bearer Tokens',
      'Rate Limiting',
      'CORS',
      'HTTPS',
      'SSL/TLS',
    ],
  },
  {
    title: 'Integration',
    icon: PhGitBranch,
    technologies: [
      'Stripe API',
      'PayPal API',
      'Google API',
      'GitHub API',
      'Webhooks',
      'Third-party APIs',
      'API Documentation',
      'Postman',
    ],
  },
])

const apiServices = ref([
  {
    icon: PhLink,
    title: 'RESTful API Development',
    description: 'Clean, well-structured REST APIs that follow industry standards and best practices for maximum compatibility.',
  },
  {
    icon: PhStack,
    title: 'GraphQL APIs',
    description: 'Flexible GraphQL endpoints that allow clients to request exactly the data they need, reducing over-fetching.',
  },
  {
    icon: PhShield,
    title: 'API Security',
    description: 'Comprehensive security implementation including authentication, authorization, and data protection.',
  },
  {
    icon: PhBook,
    title: 'API Documentation',
    description: 'Clear, comprehensive documentation with examples and interactive testing capabilities.',
  },
  {
    icon: PhGitBranch,
    title: 'Third-party Integration',
    description: 'Seamless integration with external services and APIs to extend your application capabilities.',
  },
  {
    icon: PhActivity,
    title: 'API Monitoring',
    description: 'Real-time monitoring, logging, and analytics to ensure optimal API performance and reliability.',
  },
])
</script>
